import { cn } from '@goaltree/ui/lib/utils'
import { Leaf, type LucideIcon } from 'lucide-react'
import {
  AnimatePresence,
  type AnimationPlaybackControls,
  type MotionValue,
  animate,
  motion,
  useMotionValue,
  useReducedMotion,
  useTransform,
} from 'motion/react'
import { type PointerEvent, type ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

const HOLD_MS = 900
const MOVE_TOLERANCE = 10
const HINT_KEY = 'goaltree-hold-hint-count'
const HINT_TIMES = 2

function maybeShowHint(text: string) {
  try {
    const n = Number(localStorage.getItem(HINT_KEY) ?? 0)
    if (n >= HINT_TIMES) return
    localStorage.setItem(HINT_KEY, String(n + 1))
  } catch {
    return
  }
  toast(text, { id: 'hold-to-water-hint', icon: <Leaf className="size-4 text-primary-text" /> })
}

// Press-and-hold check-in. Spread `handlers` on the existing control, put
// `water` inside it (the control needs `relative`), and skip its onClick when
// `swallowClick()` is true: pointer taps are swallowed (the hold completes instead),
// while keyboard / assistive-tech clicks still complete immediately.
export function useHoldToWater({
  enabled,
  onComplete,
  tone = 'text-primary/45',
  rounded = 'rounded-full',
  lift,
}: {
  enabled: boolean
  onComplete: () => void
  tone?: string
  rounded?: string
  // Small controls sit under the thumb: show this icon filling above the finger.
  lift?: { icon: LucideIcon; fill: string }
}) {
  const { t } = useTranslation()
  const reduce = useReducedMotion()
  const progress = useMotionValue(0)
  const [active, setActive] = useState(false)
  const [burst, setBurst] = useState(0)
  const anim = useRef<AnimationPlaybackControls | null>(null)
  const start = useRef<{ x: number; y: number } | null>(null)
  const tracking = useRef(false)
  const swallowUntil = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const armed = useRef(false)
  const [anchor, setAnchor] = useState<{ x: number; y: number } | null>(null)
  const completeRef = useRef(onComplete)
  completeRef.current = onComplete

  useEffect(
    () => () => {
      anim.current?.stop()
      if (timer.current) clearTimeout(timer.current)
    },
    [],
  )

  const drain = (delay = 0) => {
    anim.current?.stop()
    anim.current = animate(progress, 0, { duration: 0.25, delay, ease: 'easeOut' })
  }

  const cancel = (hint: boolean) => {
    if (!start.current) return
    start.current = null
    if (timer.current) clearTimeout(timer.current)
    setActive(false)
    drain()
    if (hint) maybeShowHint(t('holdToWater.hint'))
  }

  // Always attached: once the hold lands the control flips to "done" (enabled
  // false), and the trailing pointerup/click must not undo it.
  const handlers = {
    onPointerDown: (e: PointerEvent) => {
      armed.current = enabled
      if (!enabled || e.button !== 0) return
      e.stopPropagation()
      if (lift && e.pointerType !== 'mouse') {
        const r = e.currentTarget.getBoundingClientRect()
        setAnchor({ x: r.left + r.width / 2, y: r.top })
      }
      tracking.current = true
      start.current = { x: e.clientX, y: e.clientY }
      setActive(true)
      const remaining = HOLD_MS * (1 - progress.get())
      anim.current?.stop()
      anim.current = animate(progress, 1, { duration: remaining / 1000, ease: 'linear' })
      // A timer, not the fill animation, decides completion: rAF-driven
      // animations stall in throttled or background tabs.
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => {
        if (!start.current) return
        start.current = null
        setActive(false)
        setBurst((b) => b + 1)
        drain(0.3)
        completeRef.current()
      }, remaining)
    },
    onPointerMove: (e: PointerEvent) => {
      const s = start.current
      if (s && Math.hypot(e.clientX - s.x, e.clientY - s.y) > MOVE_TOLERANCE) cancel(false)
    },
    onPointerUp: () => {
      if (!tracking.current) return
      tracking.current = false
      swallowUntil.current = Date.now() + 500
      cancel(true)
    },
    onPointerLeave: () => {
      tracking.current = false
      cancel(false)
    },
    onPointerCancel: () => {
      tracking.current = false
      cancel(false)
    },
    // Keeps a held press from opening the row's long-press / right-click menu.
    onContextMenu: (e: { preventDefault: () => void; stopPropagation: () => void }) => {
      if (!enabled && !armed.current) return
      e.preventDefault()
      e.stopPropagation()
    },
  }

  // True when the caller should skip its click handler (the click that ends a
  // pointer hold). Keyboard and assistive-tech clicks pass through.
  const swallowClick = () => {
    const skip = Date.now() < swallowUntil.current
    swallowUntil.current = 0
    return skip
  }

  const water = (
    <WaterFill progress={progress} active={active} reduce={!!reduce} tone={tone} rounded={rounded}>
      {burst > 0 && !reduce && <LeafBurst key={burst} />}
      {lift &&
        createPortal(
          <AnimatePresence>
            {active && anchor && (
              <LiftBubble key="lift" progress={progress} anchor={anchor} {...lift} />
            )}
          </AnimatePresence>,
          document.body,
        )}
    </WaterFill>
  )

  return { handlers, water, swallowClick, holding: active }
}

function WaterFill({
  progress,
  active,
  reduce,
  tone,
  rounded,
  children,
}: {
  progress: ReturnType<typeof useMotionValue<number>>
  active: boolean
  reduce: boolean
  tone: string
  rounded: string
  children?: ReactNode
}) {
  const height = useTransform(progress, (v) => `${v * 100}%`)
  return (
    <span aria-hidden className="pointer-events-none absolute inset-0">
      <span className={cn('absolute inset-0 overflow-hidden', rounded, tone)}>
        <motion.span className="absolute inset-x-0 bottom-0 bg-current" style={{ height }}>
          {active && !reduce && (
            <motion.svg
              viewBox="0 0 40 6"
              preserveAspectRatio="none"
              className="absolute bottom-full left-0 h-1.5 w-[200%] fill-current"
              animate={{ x: ['0%', '-50%'] }}
              transition={{ duration: 0.8, ease: 'linear', repeat: Infinity }}
            >
              <path d="M0 3 Q5 0 10 3 T20 3 T30 3 T40 3 V6 H0 Z" />
            </motion.svg>
          )}
        </motion.span>
      </span>
      {children}
    </span>
  )
}

// The held icon, enlarged above the finger and filling as the hold progresses.
function LiftBubble({
  progress,
  anchor,
  icon: Icon,
  fill,
}: {
  progress: MotionValue<number>
  anchor: { x: number; y: number }
  icon: LucideIcon
  fill: string
}) {
  const clipPath = useTransform(progress, (v) => `inset(${(1 - v) * 100}% 0 0 0)`)
  return (
    <motion.span
      aria-hidden
      initial={{ opacity: 0, y: 10, scale: 0.6 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -6, scale: 1.25, transition: { duration: 0.25 } }}
      transition={{ type: 'spring', stiffness: 500, damping: 28 }}
      className="pointer-events-none fixed z-[100] flex size-16 items-center justify-center rounded-full border border-border bg-card shadow-lg"
      style={{ left: anchor.x, top: Math.max(anchor.y - 84, 8), x: '-50%' }}
    >
      <Icon className="absolute size-9 text-muted-foreground/40" strokeWidth={1.8} />
      <motion.span
        className="absolute inset-0 flex items-center justify-center"
        style={{ clipPath }}
      >
        <Icon className={cn('size-9', fill)} strokeWidth={1.8} />
      </motion.span>
    </motion.span>
  )
}

const LEAVES = [-90, -30, 30, 150, 210]

function LeafBurst() {
  return (
    <span className="absolute inset-0 flex items-center justify-center">
      {LEAVES.map((deg, i) => {
        const r = (deg * Math.PI) / 180
        return (
          <motion.span
            key={deg}
            className="absolute text-primary"
            initial={{ x: 0, y: 0, scale: 0.3, opacity: 1, rotate: deg }}
            animate={{
              x: Math.cos(r) * 18,
              y: Math.sin(r) * 18,
              scale: 1,
              opacity: 0,
              rotate: deg + (i % 2 ? 40 : -40),
            }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <Leaf className="size-2.5 fill-current" strokeWidth={2} />
          </motion.span>
        )
      })}
    </span>
  )
}
